import {
  createContext,
  JSX,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useQueryClient } from '@tanstack/react-query';
import { getProcessEnvUrl } from '@/utils/getUrl.ts';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector.tsx';
import { fetchData, translate, ValidationError } from '@/utils';
import * as R from '@/Router/routes';
import { resolveIsLegacyLogin } from '@/utils/fetchData.ts';

type Props = {
  children: ReactNode;
};

type LoginResponse = {
  authorizationToken: string;
  fromTemporaryPassword: boolean;
  accessVerificationRequired?: boolean;
};

type AuthContextType = {
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  clearStorage: () => void;
  postAccessVerification: (
    code: string,
    encodedCredentials: string,
  ) => Promise<LoginResponse>;
  resend: (encodedCredentials: string) => void;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function LegacyAuthProvider({ children }: Props): JSX.Element {
  const { reset } = useEventCollector();
  const queryClient = useQueryClient();
  const resend = useCallback(async (encodedCredentials: string) => {
    const path = '/token/basic';
    await fetchData(path, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Basic ${encodedCredentials}`,
      },
    });
  }, []);

  const postAccessVerification = useCallback(
    async (accessCode: string, encodedCredentials: string) => {
      const path = '/token/access-verification';
      const resolvedIsLegacyLogin = await resolveIsLegacyLogin();
      const baseUrl = resolvedIsLegacyLogin
        ? 'LEGACY_LOGIN_API'
        : 'WEB_API_URL';
      const url = `${getProcessEnvUrl(baseUrl)}${path}`;

      const res = await fetch(url, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${encodedCredentials}`,
        },
        body: `accessCode=${accessCode}`,
      });

      if (!res.ok) {
        try {
          const data = await res.json();

          datadogLogs.logger.error(`request: ${path}`, {
            request_url: url,
            error: data,
          });
        } catch (exception) {
          datadogLogs.logger.error(`request: ${path} response parse`, {
            request_url: url,
            error: exception,
          });
        }

        if (res.status === 400 || res.status === 401) {
          throw new Error(translate('error.validation.incorrect_code'));
        }

        if (res.status === 406) {
          throw new Error(translate('error.token_expired'));
        }

        if (res.status === 429) {
          throw new Error(translate('error.validation.too_many_attempts'));
        }

        throw new Error(translate('error.generic'));
      }

      const data = await res.json();

      const authorizationToken = btoa(`${data.username}:${data.token}`);
      const { fromTemporaryPassword } = data;

      sessionStorage.setItem('token', authorizationToken);
      sessionStorage.setItem('fromTemporaryPassword', fromTemporaryPassword);

      return {
        authorizationToken,
        fromTemporaryPassword,
      };
    },
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    try {
      const encodedCredentials = btoa(`${email}:${password}`);
      const path = '/token/basic';
      const resolvedIsLegacyLogin = await resolveIsLegacyLogin();
      const baseUrl = resolvedIsLegacyLogin
        ? 'LEGACY_LOGIN_API'
        : 'WEB_API_URL';
      const url = `${getProcessEnvUrl(baseUrl)}${path}`;

      const res = await fetch(url, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Basic ${encodedCredentials}`,
        },
      });

      if (!res.ok) {
        try {
          const data = await res.json();

          datadogLogs.logger.error(`request: ${path}`, {
            request_url: url,
            error: data,
          });
        } catch (exception) {
          datadogLogs.logger.error(`request: ${path} response parse`, {
            request_url: url,
            error: exception,
          });
        }

        if (res.status === 401) {
          throw new Error(translate('error.validation.unauthorized'));
        }

        if (res.status === 403) {
          throw new Error(translate('error.validation.login_unavailable'));
        }

        throw new Error(translate('error.generic'));
      }

      const data = await res.json();

      const authorizationToken = btoa(`${data.username}:${data.token}`);
      const { fromTemporaryPassword } = data;
      const { accessVerificationRequired } = data;

      sessionStorage.setItem('token', authorizationToken);
      sessionStorage.setItem('fromTemporaryPassword', fromTemporaryPassword);

      return {
        authorizationToken,
        fromTemporaryPassword,
        accessVerificationRequired,
      };
    } catch (e) {
      if (e instanceof ValidationError) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw e.message || translate('error.generic');
      }
      throw e;
    }
  }, []);

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('fromTemporaryPassword');
  }, []);

  const logout = useCallback(
    async (redirect = true) => {
      try {
        if (sessionStorage.getItem('token')) {
          sessionStorage.removeItem('discountBannerShowed');
          await fetchData('/token/invalidate', {
            method: 'delete',
          });

          clearStorage();
        }
        // eslint-disable-next-line no-useless-catch
      } catch (e) {
        throw e;
      } finally {
        // If invalidate request fails for some reason, at least remove token
        clearStorage();

        if (redirect) {
          const homeURL =
            import.meta.env.NODE_ENV === 'development'
              ? R.LOGIN
              : import.meta.env.VITE_STATIC_HOMEPAGE || R.LOGIN;
          queryClient.invalidateQueries();
          reset();
          window.location.assign(homeURL);
        }
      }
    },
    [clearStorage],
  );

  const profileContextValue = useMemo(
    () => ({
      login,
      logout,
      clearStorage,
      postAccessVerification,
      resend,
    }),
    [login, logout, clearStorage, postAccessVerification, resend],
  );

  return (
    <AuthContext.Provider value={profileContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useLegacyLoginAuth = (): AuthContextType =>
  useContext(AuthContext);
